import React, { useCallback, useContext, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { DataRoom } from 'router/subrouters/DataRoom/models/DataRoom';
import { Field, Form } from 'react-final-form';
import ManageQuestionSubjectsDrawer from 'router/subrouters/DataRoom/components/ManageQuestionSubjectsDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import TeamsField from 'components/TeamsField';
import { MAX_NUM_OF_TEAMS } from 'components/TeamsField/TeamsField.component';
import TextAreaField from 'components/TextAreaField';
import InputField from 'components/InputField';
import validations from 'validations';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import CheckToggleField from 'components/CheckToggleField';
import DurationDaysField from 'components/DurationDaysField';
import { Button } from 'ncoded-component-library';
import SettingsIcon from 'icons/Settings.icon';
import DefaultsSettingsTag from 'router/subrouters/DataRoom/components/DefaultsSettingsTag';
import ApplyToAllDataRoomsModal from 'router/subrouters/DataRoom/components/ApplyToAllDataRoomsModal';
import SoftSignalsLockedHint from 'router/subrouters/DataRoom/components/SoftSignalsLockedHint';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import {
  INITIAL_FILE_ACCESS_DAYS,
  INITIAL_EXTEND_ACCESS_DAYS,
  SESSION_TIMEOUT_KEY,
  MIN_SESSION_MINUTES,
  MAX_SESSION_MINUTES,
  SESSION_TIMEOUT_PRESETS,
  MIN_DAYS_FOR_FILE_ACCESS,
} from './data';

import './DataRoomSetup.styles.scss';
import './DataRoomSetup.styles.responsive.scss';

type DataRoomSetupProps = {
  className?: string;
};

const DataRoomSetup: React.FC<DataRoomSetupProps> = (props) => {
  const { className } = props;

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const { doesntHaveAccess, dataRoom, setDataRoom } =
    useContext(DataRoomContext);

  const {
    _id,
    defaultsSettingsSetupAt,
    defaultsSettingsSetupBy,
    isSoftSignalsLocked,
  } = dataRoom;

  const useDefaults = useRef(false); // true when "Save as default for all DR" is clicked

  const [manageQuestionSubjectDrawer, manageQuestionSubjectDrawerRef] =
    useCallbackRef<SideDrawerRef | null>(null);
  const [applyToAllDataRoomsModal, applyToAllDataRoomsModalRef] =
    useCallbackRef<ModalRef>();

  const classes = classNames('data-room-setup', className);

  const { t } = useTranslation();

  const initialValues = useMemo(() => {
    const {
      teams,
      customWelcomeMessage,
      sessionTimeout,
      accessConfirmation = true,
      accessConfirmationDays = INITIAL_FILE_ACCESS_DAYS,
      buySideCanInviteColleagues = false,
      canRenewFileAccess = false,
      renewFileAccessDays = INITIAL_EXTEND_ACCESS_DAYS,
      allowSoftSignals = false,
    } = dataRoom;

    return {
      customWelcomeMessage,
      sessionTimeout,
      buySideCanInviteColleagues,
      accessConfirmation,
      accessConfirmationDays,
      canRenewFileAccess,
      renewFileAccessDays,
      allowSoftSignals,
      teams: teams || [],
    };
  }, [dataRoom]);

  const validate = useCallback(
    ({
      accessConfirmation,
      accessConfirmationDays,
      canRenewFileAccess,
      renewFileAccessDays,
    }: DataRoom) => {
      const errors = {} as Record<keyof DataRoom, string>;

      if (accessConfirmation && !accessConfirmationDays)
        errors.accessConfirmationDays = t('requiredField');

      if (canRenewFileAccess && !renewFileAccessDays)
        errors.renewFileAccessDays = t('requiredField');

      return errors;
    },
    [t],
  );

  const handleSubmit = useCallback(
    async (values: DataRoom) => {
      if (doesntHaveAccess) return;

      const {
        accessConfirmation,
        accessConfirmationDays,
        canRenewFileAccess,
        renewFileAccessDays,
        ...rest
      } = values;

      const newValues = {
        ...rest,
        accessConfirmation,
        canRenewFileAccess,
        ...(accessConfirmation &&
          accessConfirmationDays && {
            accessConfirmationDays: +accessConfirmationDays,
          }),
        ...(canRenewFileAccess &&
          renewFileAccessDays && {
            renewFileAccessDays: +renewFileAccessDays,
          }),
      };

      const isDefault = useDefaults.current;

      try {
        const { data } = await dataRoomApi.room.patchDataRoom(
          _id,
          newValues,
          isDefault,
        );
        setDataRoom((prev) => ({
          ...prev,
          ...data,
          defaultsSettingsSetupAt: data.defaultsSettingsSetupAt,
          defaultsSettingsSetupBy: data.defaultsSettingsSetupBy,
        }));
        popSuccess(
          t(
            isDefault
              ? 'saveAsOrganisationDefaultsSuccessMessage'
              : 'saveDataRoomSuccessMessage',
          ),
        );
      } catch (e) {
        popServerError(e);
      }
    },
    [doesntHaveAccess, _id, setDataRoom, popSuccess, t, popServerError],
  );

  return (
    <div className={classes}>
      <Form
        onSubmit={handleSubmit}
        validate={validate}
        initialValues={initialValues}
        render={({
          pristine,
          submitting,
          values: { accessConfirmation, canRenewFileAccess, sessionTimeout },
          form: { submit, change },
          handleSubmit,
        }) => (
          <form className="white-panel" onSubmit={handleSubmit}>
            <div className="heading">
              <p className="name">{t('setUpDataRoom')}</p>
              <DefaultsSettingsTag
                defaultsSettingsSetupAt={defaultsSettingsSetupAt}
                defaultsSettingsSetupBy={defaultsSettingsSetupBy}
              />
            </div>

            <div className="row">
              <Field
                name="teams"
                component={TeamsField}
                label={t('teamsTitle', { count: MAX_NUM_OF_TEAMS })}
                description={t('teamsHint')}
                disabled={doesntHaveAccess}
              />
            </div>

            <div className="row">
              <Field
                label={t('customEmailMessage')}
                name="customWelcomeMessage"
                component={TextAreaField}
                disabled={doesntHaveAccess}
              />

              <div className="buy-side-permissions">
                <p>{t('buySidePermissions')}</p>
                <Field
                  name="buySideCanInviteColleagues"
                  component={CheckboxField}
                  type="checkbox"
                  label={t('buySideInviteColleagues')}
                  disabled={doesntHaveAccess}
                />
              </div>
            </div>

            <div className="row">
              <div className="session-timeout-field">
                <Field
                  name={SESSION_TIMEOUT_KEY}
                  component={InputField}
                  disabled={doesntHaveAccess}
                  label={t('sessionTimeoutMinutes')}
                  validate={validations.composeValidators(
                    validations.minNumber(
                      t('mustBeGreaterThan', { count: MIN_SESSION_MINUTES }),
                      MIN_SESSION_MINUTES,
                    ),
                    validations.maxNumber(
                      t('mustBeLowerThanString', {
                        value: t('sessionTimeout24h'),
                      }),
                      MAX_SESSION_MINUTES,
                    ),
                  )}
                  required
                />
                <div className="session-timeout-presets">
                  {SESSION_TIMEOUT_PRESETS.map(({ label, value }) => (
                    <Button
                      key={value}
                      type="button"
                      disabled={doesntHaveAccess}
                      className={classNames({
                        active: +sessionTimeout === value,
                      })}
                      onClick={() => change(SESSION_TIMEOUT_KEY, value)}
                    >
                      {t(label)}
                    </Button>
                  ))}
                </div>
                <p>
                  {t('theMinDuration', {
                    count: MIN_SESSION_MINUTES,
                  })}
                </p>
              </div>

              <div className="access-confirmation">
                <div className="access-confirmation__title">
                  {t('accessConfirmationSettings')}
                  <ResolvePopup
                    onHover
                    popupContent={t('accessConfirmationSettingsPopup')}
                  >
                    <InformationcircleIcon />
                  </ResolvePopup>
                </div>

                <div className="file-access-extension">
                  <Field
                    name="accessConfirmation"
                    type="checkbox"
                    component={CheckToggleField}
                    disabled={doesntHaveAccess}
                  />

                  <DurationDaysField
                    name="accessConfirmationDays"
                    label={t('requireBuyersReConfirm')}
                    disabled={!accessConfirmation}
                    minDays={MIN_DAYS_FOR_FILE_ACCESS}
                    copyForMinValidation="minimumConfirmationCycleInDays"
                    copyForMaxValidation="maximumConfirmationCycleInDays"
                    onLabelClick={
                      doesntHaveAccess
                        ? undefined
                        : () =>
                            change('accessConfirmation', !accessConfirmation)
                    }
                  />
                </div>

                {accessConfirmation && (
                  <div className="file-access-extension">
                    <Field
                      name="canRenewFileAccess"
                      type="checkbox"
                      component={CheckToggleField}
                      disabled={doesntHaveAccess}
                    />

                    <DurationDaysField
                      disabled={!canRenewFileAccess}
                      name="renewFileAccessDays"
                      label={t('allowBuyersToExtendFileAccess')}
                      copyForMinValidation="minimumExtentionCycleInDays"
                      copyForMaxValidation="maximumExtentionCycleInDays"
                      onLabelClick={
                        doesntHaveAccess
                          ? undefined
                          : () =>
                              change('canRenewFileAccess', !canRenewFileAccess)
                      }
                    >
                      {t('onTheirOwn')}
                    </DurationDaysField>
                  </div>
                )}
              </div>
            </div>

            <div className="row">
              <div
                className={classNames('soft-signals-section', {
                  'soft-signals-section--locked': isSoftSignalsLocked,
                })}
              >
                <div className="soft-signals-section__title">
                  {t('softSignals')}
                  <ResolvePopup onHover popupContent={t('trackBuyerInterest')}>
                    <InformationcircleIcon />
                  </ResolvePopup>
                </div>

                <Field
                  name="allowSoftSignals"
                  component={CheckToggleField}
                  type="checkbox"
                  label={t('allowSoftSignalsForAllFiles')}
                  disabled={doesntHaveAccess || isSoftSignalsLocked}
                />

                {isSoftSignalsLocked && <SoftSignalsLockedHint />}
              </div>

              {!doesntHaveAccess && (
                <div className="manage-q-a">
                  <p>{t('qaTopics')}</p>
                  <Button
                    variant="link"
                    icon={<SettingsIcon strokeWidth={2} />}
                    onClick={() => manageQuestionSubjectDrawer?.setOpen(true)}
                  >
                    {t('editQATopics')}
                  </Button>
                </div>
              )}
            </div>

            <div className="actions">
              <Button
                variant="outline"
                disabled={doesntHaveAccess || pristine || submitting}
                onClick={() => {
                  useDefaults.current = true;
                  applyToAllDataRoomsModal?.open();
                }}
              >
                {t('saveAsDefaultsForAllDataRooms')}
              </Button>

              <Button
                disabled={doesntHaveAccess || pristine || submitting}
                onClick={() => {
                  useDefaults.current = false;
                  submit();
                }}
              >
                {t('saveToThisDataRoom')}
              </Button>
            </div>

            <ApplyToAllDataRoomsModal
              ref={applyToAllDataRoomsModalRef}
              onSubmit={() => {
                submit();
                applyToAllDataRoomsModal?.close();
              }}
            />
          </form>
        )}
      />

      <ManageQuestionSubjectsDrawer ref={manageQuestionSubjectDrawerRef} />
    </div>
  );
};

export default DataRoomSetup;
